import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { not, readOnly } from '@ember/object/computed';
import Controller, { inject as controller } from '@ember/controller';
import FeaturesService from 'ember-feature-flags/services/features';
import { dropTask, timeout } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import PersonActivityController from 'cio/pods/person/activity/controller';
import PersonIndexController from 'cio/pods/person/index/controller';
import { IdentifierKey } from 'cio/utils/identifiers';
import config from 'cio/config/environment';
import DS from 'ember-data';
import ObjectTypesService from 'cio/services/object-types';
import CustomerModel from 'cio/models/customer';
import RelationshipsService from 'cio/services/relationships';

export default class PersonController extends Controller {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service features!: FeaturesService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service objectTypes!: ObjectTypesService;
  @service('relationships') relationshipsService!: RelationshipsService;

  declare model: CustomerModel;

  @readOnly('model.attrs.email') email!: string;
  @readOnly('model.attrs.first_name') firstName!: string;
  @readOnly('model.attrs.last_name') lastName!: string;
  @readOnly('model.attrs.created_at') signedUp!: number;
  @readOnly('model.fullName') fullName!: string;
  @not('model.unsubscribed') isSubscribed!: boolean;
  @controller('person.index') personIndexController!: PersonIndexController;
  @controller('person.activity') personActivityController!: PersonActivityController;

  @tracked deletedPersonCount = 1;
  @tracked showDeleteModal = false;
  @tracked showExportModal = false;
  @tracked isShowingSendTestEventModal = false;
  @tracked isShowingExportProgress = false;
  @tracked showRelationshipsModal = false;

  exportPendingImage = new URL('cio/assets/images/export-pending.svg', import.meta.url).toString();
  exportSuccessImage = new URL('cio/assets/images/export-success.svg', import.meta.url).toString();
  exportFailureImage = new URL('cio/assets/images/export-failure.svg', import.meta.url).toString();

  get optionsDropdownOptions() {
    let options = [];

    if (this.model.unsubscribed) {
      options.push({
        label: 'Subscribe',
        action: this.subscribe.bind(this),
      });
    } else {
      options.push({
        label: 'Unsubscribe',
        action: this.unsubscribe.bind(this),
      });
    }

    if (this.auth.hasPermission('journeys/exports/create')) {
      options.push({
        label: 'Export profile data',
        action: () => this.set('showExportModal', true),
      });
    }

    if (this.auth.hasPermission('journeys/people/edit')) {
      options.push({
        label: 'Merge with another person',
        link: {
          route: 'people.merge',
          query: { primary_id: this.model.id, source: 'people_options_dropdown' },
        },
      });
    }

    if (this.auth.hasPermission('journeys/imports/create')) {
      options.push({
        label: 'Send event',
        action: () => this.set('isShowingSendTestEventModal', true),
      });
    }

    if (this.objectTypes.objectCount > 0 && this.objectTypes.canCreateRelationships) {
      options.push({
        label: 'Add Relationships',
        action: () => (this.showRelationshipsModal = true),
      });
    }

    if (this.auth.hasPermission('journeys/people/delete')) {
      options.push({
        label: 'Delete forever',
        action: () => this.set('showDeleteModal', true),
      });
    }

    return options.map((o) => ({ ...o, classNames: 'whitespace-nowrap' }));
  }

  private subscribe() {
    let identifier = this.model.email ?? this.model.identifiers.id;
    let customerIds = [this.model.identifiers[IdentifierKey.CioId]];
    let promise = this.api.post('customers/subscribe', {
      data: { internal_ids: customerIds },
    });

    return promise
      .then(() => {
        this.model.reload();
        this.alerts.success('person.subscribe', `${identifier} will be subscribed soon`);
      })
      .catch(ignorePromiseFailure);
  }

  private unsubscribe() {
    let identifier = this.model.email ?? this.model.identifiers.id;
    let customerIds = [this.model.identifiers[IdentifierKey.CioId]];
    let promise = this.api.post('customers/unsubscribe', {
      data: { internal_ids: customerIds },
    });

    return promise
      .then(() => {
        this.model.reload();
        this.alerts.success('person.unsubscribe', `${identifier} will be unsubscribed soon`);
      })
      .catch(ignorePromiseFailure);
  }

  private deletePerson({ suppress }: { suppress: boolean }) {
    let customerIds = [this.model.identifiers[IdentifierKey.CioId]];
    let promise = this.api.post('customers/delete', {
      data: { type: 'internal_ids', internal_ids: customerIds, suppress },
    });

    this.apiErrors.attach(promise);

    promise.then(() => {
      this.router.transitionTo('people');
    }, ignorePromiseFailure);

    return promise;
  }

  deleteTask = dropTask(async ({ suppress }: { suppress: boolean }) => {
    try {
      await this.deletePerson({ suppress });
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  });

  @action
  exportPerson() {
    let data = { internal_id: this.model.identifiers[IdentifierKey.CioId] };
    return this.api.post('exports/profile_data', { data });
  }

  @action
  showExportProgressModal() {
    this.set('isShowingExportProgress', true);
    this.set('showExportModal', false);
  }

  @action
  onRelationshipsAdd() {
    this.showRelationshipsModal = false;

    this.relationshipsService.fetchRelationshipsForObjectTypes.perform({
      model: this.model,
      delay: config.APP.relationshipsChangedDelay,
    });
  }

  refreshLogs = dropTask(async () => {
    if (this.router.currentRouteName === 'person.activity') {
      this.personActivityController.fetchLogs.perform();
    } else if (this.router.currentRouteName === 'person.index') {
      this.personIndexController.fetchLogs.perform();
    } else if (this.router.currentRouteName === 'person.subjects.index') {
      this.send('refreshModel');
    }
  });

  refreshAttributesIfNecessary = dropTask(async () => {
    if (!this.alerts.current.some((a) => a.id === 'people.merge.success')) {
      return;
    }

    await timeout(config.APP.afterPeopleMergeRefreshDelay);

    try {
      await this.model.reload();
    } catch (e) {
      // ignore failure
    }
  });
}
